import { RealtimeConfig } from './config';
import { ConnectionState } from './events';
import { RealtimeHttpError } from './http-client';
import { parseSseChunk, SseFrame } from './sse-parser';
const DEFAULT_INITIAL_DELAY_MS = 1000;
const DEFAULT_MAX_DELAY_MS = 30000;
const STALE_TIMEOUT_MS = 40000;
export class SseClient {
  private abortController: AbortController | null = null;
  private closedByUser = false;
  private attempt = 0;
  private state: ConnectionState = 'closed';
  private wakeFromBackoff: (() => void) | null = null;
  private backoffTimer: ReturnType<typeof setTimeout> | null = null;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private staleReconnecting = false;
  constructor(
    private readonly config: RealtimeConfig,
    private readonly onFrame: (frame: SseFrame) => void,
    private readonly onStateChange: (state: ConnectionState) => void,
  ) {}
  connect(path: string): void {
    if (this.state !== 'closed') {
      return;
    }
    this.closedByUser = false;
    this.attempt = 0;
    void this.runLoop(path);
  }
  disconnect(): void {
    this.closedByUser = true;
    this.clearStaleTimer();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.backoffTimer) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
    if (this.wakeFromBackoff) {
      const wake = this.wakeFromBackoff;
      this.wakeFromBackoff = null;
      wake();
    }
    this.setState('closed');
  }
  private async runLoop(path: string): Promise<void> {
    while (!this.closedByUser) {
      this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
      try {
        await this.stream(path);
      } catch (error) {
        if (this.closedByUser) {
          return;
        }
        if (isAbortError(error)) {
          if (!this.staleReconnecting) {
            return;
          }
          this.staleReconnecting = false;
        } else {
          this.reportError(error, 'sse-stream');
        }
      }
      if (this.closedByUser) {
        return;
      }
      await this.backoff();
    }
  }
  private async stream(path: string): Promise<void> {
    const token = await this.config.getToken();
    this.abortController = new AbortController();
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(this.config.baseApiUrl + path, {
      method: 'GET',
      headers,
      signal: this.abortController.signal,
    });
    if (!response.ok || !response.body) {
      throw new RealtimeHttpError(
        response.status,
        `Stream connect failed with status ${response.status}`,
      );
    }
    this.attempt = 0;
    this.setState('open');
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    try {
      this.armStaleTimer();
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        this.armStaleTimer();
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseSseChunk(buffer);
        buffer = rest;
        for (const frame of frames) {
          this.onFrame(frame);
        }
      }
    } finally {
      this.clearStaleTimer();
    }
  }
  private armStaleTimer(): void {
    this.clearStaleTimer();
    this.staleTimer = setTimeout(() => {
      this.staleReconnecting = true;
      if (this.abortController) {
        this.abortController.abort();
      }
    }, STALE_TIMEOUT_MS);
  }
  private clearStaleTimer(): void {
    if (this.staleTimer) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  }
  private backoff(): Promise<void> {
    const initial =
      this.config.reconnect?.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
    const max = this.config.reconnect?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
    const delay = Math.min(max, initial * 2 ** this.attempt);
    this.attempt += 1;
    return new Promise<void>((resolve) => {
      this.wakeFromBackoff = resolve;
      this.backoffTimer = setTimeout(() => {
        this.backoffTimer = null;
        this.wakeFromBackoff = null;
        resolve();
      }, delay);
    });
  }
  private setState(state: ConnectionState): void {
    if (this.state === state) {
      return;
    }
    this.state = state;
    this.onStateChange(state);
  }
  private reportError(error: unknown, context: string): void {
    if (this.config.onError) {
      this.config.onError(error, context);
    } else {
      console.error(`[bay-manager-realtime] ${context}`, error);
    }
  }
}
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (
      error as {
        name?: string;
      }
    ).name === 'AbortError'
  );
}
